/**
 * Global Interval Coordinator
 * Prevents setInterval conflicts and long tasks by:
 * 1. Staggering heavy operations 
 * 2. Mutex locks for critical operations
 * 3. Smart scheduling based on device capability
 * 4. Unified cleanup management
 */

import deviceCapabilityDetector from './deviceCapabilityDetector';

class IntervalCoordinator {
  constructor() {
    this.intervals = new Map();
    this.activeMutexes = new Set();
    this.operationQueue = [];
    this.isProcessingQueue = false;
    this.deviceInfo = deviceCapabilityDetector.deviceInfo;
    
    // Base timing offsets to prevent overlaps (staggered every 15s)
    this.baseOffsets = {
      MEMORY_MANAGEMENT: 0,      // Start immediately  
      DATA_SYNC: 15000,          // +15s offset
      PERFORMANCE_MONITOR: 30000, // +30s offset  
      STORAGE_CLEANUP: 45000,    // +45s offset
      IMAGE_CLEANUP: 60000,      // +60s offset
      NETWORK_MONITOR: 75000     // +75s offset (1min 15s)
    };
  }

  devLog(message, ...args) {
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log(message, ...args);
      }
    } catch (e) {
      // Handle process undefined in webpack hot reload
      console.log(message, ...args);
    }
  }

  /**
   * Register an interval with coordination
   */
  registerInterval(key, callback, baseInterval, options = {}) {
    const {
      mutex = null,
      priority = 'NORMAL',
      maxExecutionTime = 16,
      deviceScaling = true
    } = options;

    // Clear existing interval if re-registering
    if (this.intervals.has(key)) {
      this.clearInterval(key);
    }

    // Apply device-based scaling
    let adjustedInterval = baseInterval;
    if (deviceScaling) {
      const { tier } = this.deviceInfo;
      if (tier === 'LOW_END') {
        adjustedInterval = Math.max(baseInterval * 1.5, baseInterval + 30000); // Slower on weak devices
      } else if (tier === 'HIGH_END') {
        adjustedInterval = Math.max(baseInterval * 0.8, 10000); // Faster on powerful devices
      }
    }

    // Apply staggered offset based on operation type
    const offset = this.baseOffsets[priority] || 0;
    
    // Wrap callback with mutex and performance monitoring
    const wrappedCallback = async () => {
      const startTime = performance.now();
      
      // Check mutex if specified
      if (mutex && this.activeMutexes.has(mutex)) {
        this.devLog(`[IntervalCoordinator] Skipping ${key} - mutex ${mutex} active`);
        return;
      }

      try {
        // Acquire mutex
        if (mutex) {
          this.activeMutexes.add(mutex);
        }

        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error(`Timeout after ${maxExecutionTime}ms`)), maxExecutionTime * 10);
        });

        await Promise.race([
          callback(),
          timeoutPromise
        ]);

        const executionTime = performance.now() - startTime;
        
        // Warn if operation took too long
        if (executionTime > maxExecutionTime) {
          console.warn(`[IntervalCoordinator] ${key} took ${executionTime.toFixed(1)}ms (limit: ${maxExecutionTime}ms)`);
        }

      } catch (error) {
        if (!error.message?.includes('Timeout after')) {
          console.error(`[IntervalCoordinator] Error in ${key}:`, error);
        }
      } finally {
        // Release mutex
        if (mutex) {
          this.activeMutexes.delete(mutex);
        }
      }
    };

    // Schedule first execution with offset, then use normal interval
    const timeoutId = setTimeout(() => {
      wrappedCallback(); // Execute first time
      
      // Then set up regular interval
      const intervalId = setInterval(wrappedCallback, adjustedInterval);
      
      // Store the interval ID for cleanup
      this.intervals.set(key, {
        intervalId,
        timeoutId: null,
        mutex,
        priority,
        baseInterval,
        adjustedInterval,
        callback: wrappedCallback
      });
      
    }, offset);

    // Store timeout ID temporarily
    this.intervals.set(key, {
      intervalId: null,
      timeoutId,
      mutex,
      priority, 
      baseInterval,
      adjustedInterval,
      callback: wrappedCallback
    });

    this.devLog(`[IntervalCoordinator] Registered ${key}: ${adjustedInterval}ms interval with ${offset}ms offset`);
  }

  /**
   * Clear specific interval
   */
  clearInterval(key) {
    const intervalInfo = this.intervals.get(key);
    if (intervalInfo) {
      if (intervalInfo.intervalId) {
        clearInterval(intervalInfo.intervalId);
      }
      if (intervalInfo.timeoutId) {
        clearTimeout(intervalInfo.timeoutId);
      }
      
      // Release mutex if held
      if (intervalInfo.mutex) {
        this.activeMutexes.delete(intervalInfo.mutex);
      }
      
      this.intervals.delete(key);
      this.devLog(`[IntervalCoordinator] Cleared interval: ${key}`);
    }
  }

  /**
   * Clear all intervals (app cleanup)
   */
  clearAllIntervals() {
    for (const key of this.intervals.keys()) {
      this.clearInterval(key);
    }
    this.activeMutexes.clear();
    this.devLog('[IntervalCoordinator] All intervals cleared');
  }

  /**
   * Get coordination stats
   */
  getStats() {
    return {
      activeIntervals: this.intervals.size,
      activeMutexes: this.activeMutexes.size,
      queuedOperations: this.operationQueue.length,
      deviceTier: this.deviceInfo.tier
    };
  }

  /**
   * Temporarily pause all intervals (during heavy operations)
   */
  pauseAll() {
    for (const [, info] of this.intervals) {
      if (info.intervalId) {
        clearInterval(info.intervalId);
        info.paused = true;
      }
    }
    this.devLog('[IntervalCoordinator] All intervals paused');
  }

  /**
   * Resume paused intervals
   */
  resumeAll() {
    for (const [, info] of this.intervals) {
      if (info.paused) {
        info.intervalId = setInterval(info.callback, info.adjustedInterval);
        info.paused = false;
      }
    }
    this.devLog('[IntervalCoordinator] All intervals resumed');
  }
}

// Export singleton
export const intervalCoordinator = new IntervalCoordinator();
window.intervalCoordinator = intervalCoordinator;